// src/features/reporting/reporting-view.tsx
import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Alert from '@mui/material/Alert';
import { Grid } from "@material-ui/core";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ReportGrid from "./report-grid";
import { useAppState } from "../../helpers/hooks";
import LoadingIndicator from "components/loading-indicator";
import { useError, logger } from "@personicom/customizations";
import type { LogCategory } from '@personicom/customizations';

const buildStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    background: theme.palette.common.white,
    height: "100%",
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  errorList: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
}));

const ReportingView: React.FC = () => {
  const classes = buildStyles();
  const { error, clearError } = useError();
  const { reportConfig, isAppInitialized, areFiltersInitialized, configDiagnostics } = useAppState();

  const hasConfigDiagnostics = configDiagnostics.length > 0;
  const configDiagnosticsSeverity: "warning" | "error" = hasConfigDiagnostics && configDiagnostics.some((d) => d.severity === "error")
    ? "error"
    : "warning";

  useEffect(() => {
    if (error) {
      logger.error("Data fetching error", error.detail, 'General');
    }
  }, [error]);

  // Wait for essential state (reportConfig and isAppInitialized) but not areFiltersInitialized
  if (!reportConfig || !isAppInitialized) {
    return <LoadingIndicator message="Loading Grid Configuration..." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid id="reporting-view" container direction="column" className={classes.root}>
        {/* Error Alert: Show only if there's a non-blocking operational error */}
        {error && (
          <Alert severity="error" className={classes.errorAlert} onClose={clearError}>
            <strong>{error.title || "Error"}:</strong> {typeof error.detail === "string" ? error.detail : "An error occurred"}
            {error.errors && Object.keys(error.errors).length > 0 && (
              <ul className={classes.errorList}>
                {Object.entries(error.errors).map(([field, messages]) => (
                  <li key={field}>
                    <strong>{field}:</strong> {Array.isArray(messages) ? messages.join(", ") : messages}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        {hasConfigDiagnostics && (
          <Alert severity={configDiagnosticsSeverity} className={classes.errorAlert}>
            <strong>Report configuration {configDiagnosticsSeverity === "error" ? "issue" : "warning"}:</strong>
            <ul className={classes.errorList}>
              {configDiagnostics.map((diag, index) => (
                <li key={`${diag.field ?? "config"}-${index}`}>
                  {diag.field ? (<><strong>{diag.field}:</strong>&nbsp;</>) : null}
                  {diag.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {!areFiltersInitialized && <LoadingIndicator message="Initializing Filters..." />}
        <ReportGrid config={reportConfig} />
      </Grid>
    </LocalizationProvider>
  );
};

export default ReportingView;

